import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import axios from 'axios';
import { DEFAULT_MAX_RETRY, DEFAULT_RETRY_DELAY } from '@main/http/constants';
import sleep from '@main/utils/sleep';

export interface HttpClientRetryConfig {
  maxRetry?: number;
  preRetryOp?: <T extends AxiosError>(error: T) => Promise<void>;
  retryCondition?: <T extends AxiosError>(
    error: T,
    retryCount: number,
  ) => boolean;
  delay?: number | ((retryCount: number) => number);
}

export interface HttpClientOptions {
  baseUrl: string;
  headers?: AxiosRequestConfig['headers'];
  retryConfig?: HttpClientRetryConfig;
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    retryConfig?: HttpClientRetryConfig;
    retryCount?: number;
  }
}

function defaultRetryCondition(): boolean {
  return false;
}

function noop(): undefined {
  return;
}

export class HttpClient {
  protected axiosInstance: AxiosInstance;
  protected defaultHeaders: AxiosRequestConfig['headers'] = {
    'Content-Type': 'application/json; charset=UTF-8',
    Accept: 'application/json, text/plain, */*',
  };

  constructor(protected options: HttpClientOptions) {
    const { baseUrl, headers } = this.options;
    this.axiosInstance = axios.create({
      baseURL: baseUrl,
      headers: {
        ...this.defaultHeaders,
        ...headers,
      },
    });

    this.axiosInstance.interceptors.response.use(
      undefined,
      this.retryInterceptor,
    );
  }

  protected mergeConfig(
    retryConfig: HttpClientRetryConfig,
    axiosConfig: AxiosRequestConfig,
  ) {
    return Object.assign({}, retryConfig, axiosConfig.retryConfig);
  }

  protected retryInterceptor = async (
    error: AxiosError,
  ): Promise<AxiosResponse<unknown>> => {
    const { retryConfig } = this.options;

    if (!retryConfig) {
      return Promise.reject(error);
    }

    const { config } = error;

    if (!config) {
      return Promise.reject(error);
    }

    const {
      retryCondition = defaultRetryCondition,
      preRetryOp = noop,
      maxRetry = DEFAULT_MAX_RETRY,
      delay = DEFAULT_RETRY_DELAY,
    } = this.mergeConfig(retryConfig, config);

    config.retryCount = config.retryCount ?? 0;
    const shouldRetry =
      retryCondition(error, config.retryCount) && config.retryCount < maxRetry;
    const delayDuration =
      typeof delay === 'function' ? delay(config.retryCount) : delay;

    if (!shouldRetry) {
      return Promise.reject(error);
    }

    config.retryCount++;
    if (delayDuration > 0) {
      await sleep(delayDuration);
    }

    try {
      await preRetryOp(error);
      return await this.axiosInstance.request(config);
    } catch {
      return Promise.reject(error);
    }
  };

  async get<TResponseData>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<TResponseData> {
    return this.axiosInstance.get(url, config).then(this.handleResponse);
  }

  async delete<TResponseData>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<TResponseData> {
    return this.axiosInstance.delete(url, config).then(this.handleResponse);
  }

  async post<TResponseData>(
    url: string,
    payload: unknown = {},
    config?: AxiosRequestConfig,
  ): Promise<TResponseData> {
    return this.axiosInstance
      .post(url, payload, config)
      .then(this.handleResponse);
  }

  async put<TResponseData>(
    url: string,
    payload: unknown = {},
    config?: AxiosRequestConfig,
  ): Promise<TResponseData> {
    return this.axiosInstance
      .put(url, payload, config)
      .then(this.handleResponse);
  }

  async patch<TResponseData>(
    url: string,
    payload: unknown = {},
    config?: AxiosRequestConfig,
  ): Promise<TResponseData> {
    return this.axiosInstance
      .patch(url, payload, config)
      .then(this.handleResponse);
  }

  protected handleResponse<T>(
    response: AxiosResponse<T>,
  ): AxiosResponse<T>['data'] {
    return response.data;
  }
}
